import React, { useMemo } from 'react';
import { Paper, Typography, Stack, Chip, Box } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import PaymentIcon from '@mui/icons-material/Payment';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const DisputeSummaryCard = ({ reservation }) => {
  const { t } = useTranslation(['disputes', 'financial']);

  const data = useMemo(() => {
    if (!reservation) return {};

    const pickDispute = () => {
      if (reservation.dispute) return reservation.dispute;
      if (reservation.Dispute) return reservation.Dispute;
      if (Array.isArray(reservation.disputes) && reservation.disputes.length) return reservation.disputes[0];
      if (Array.isArray(reservation.Disputes) && reservation.Disputes.length) return reservation.Disputes[0];
      if (reservation.metadata?.dispute) return reservation.metadata.dispute;
      return null;
    };

    const dispute = pickDispute();
    const status =
      dispute?.status ||
      reservation.dispute_status ||
      reservation.disputeStatus ||
      reservation.metadata?.dispute_status ||
      reservation.metadata?.disputeStatus ||
      null;

    const proposal =
      dispute?.metadata?.amicable_proposal ||
      dispute?.amicable_proposal ||
      reservation?.metadata?.amicable_proposal ||
      reservation?.metadata?.proposal ||
      null;

    const payments = (() => {
      const fromDetails = Object.values(reservation.payment_details || {}).flatMap((d) => d.payments || []);
      const fromArrayHistory = Array.isArray(reservation.payment_history) ? reservation.payment_history : [];
      const fromObjectHistory = Array.isArray(reservation.payment_history?.payments) ? reservation.payment_history.payments : [];
      const direct = Array.isArray(reservation.payments) ? reservation.payments : [];
      return [...direct, ...fromArrayHistory, ...fromObjectHistory, ...fromDetails];
    })();

    const compensationTypes = [
      'damage_compensation',       
      'dispute_refund' 
    ];

    const latestComp = payments
      .filter((p) => compensationTypes.includes(p.payment_type || p.type))
      .sort((a, b) => new Date(b.created_at || b.transaction_date || 0) - new Date(a.created_at || a.transaction_date || 0))[0];

    const hasDisputeFlag = Boolean(
      dispute ||
        reservation.dispute_id ||
        reservation.disputeId ||
        reservation.metadata?.dispute_id ||
        (Array.isArray(reservation.disputes) && reservation.disputes.length > 0) ||
        (Array.isArray(reservation.Disputes) && reservation.Disputes.length > 0)
    );

    return { dispute, status, proposal, latestComp, hasDisputeFlag };
  }, [reservation]);

  if (!data.status && !data.latestComp && !data.proposal && !data.hasDisputeFlag) {
    return null;
  }

  const statusLabel = data.status
    ? t(`status.${data.status}`, data.status)
    : data.hasDisputeFlag
      ? t('disputes:detail.progressTitle', 'Progression du litige')
      : t('detail.unknownUser', 'Statut inconnu');
  const proposalLabel = data.proposal?.status ? t(`status.${data.proposal.status}`, data.proposal.status) : null;
  const proposalType = data.proposal?.compensation_type || data.proposal?.type;
  const proposalAmount = data.proposal?.amount;
  const proposalCurrency = data.proposal?.currency || reservation?.currency || 'EUR';

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return format(new Date(d), 'dd/MM/yyyy HH:mm');
    } catch (e) {
      return '';
    }
  };

  return (
    <Paper elevation={0} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <GavelIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t('detail.resolutionTitle', 'Résolution')}
        </Typography>
        {data.status && (
          <Chip label={statusLabel} size="small" color={data.status === 'escalated' ? 'warning' : data.status === 'resolved' ? 'success' : 'default'} />
        )}
      </Stack>

      {data.proposal && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {t('financial:payment.currentProposal', 'Proposition amiable')} {proposalLabel ? `• ${proposalLabel}` : ''}
            {proposalType ? ` • ${t(`compensationTypes.${proposalType}`, proposalType)}` : ''}
            {proposalAmount != null ? ` • ${proposalAmount} ${proposalCurrency}` : ''}
          </Typography>
        </Stack>
      )}

      {data.latestComp && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <PaymentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {t(`dashboard:reservationDetails.paymentTypes.${data.latestComp.payment_type || data.latestComp.type}`, 'Compensation')} • {data.latestComp.amount} {data.latestComp.currency || reservation?.currency || 'EUR'} • {data.latestComp.status ? t(`paymentStatus.${data.latestComp.status}`, data.latestComp.status) : '-'}
            {data.latestComp.created_at || data.latestComp.transaction_date ? ` • ${formatDate(data.latestComp.created_at || data.latestComp.transaction_date)}` : ''}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

export default DisputeSummaryCard;
